import { PowerProfileDataPoint, allDays } from 'greener-ui-library';
import { aggregateDataByMinute } from './powerRequirementsUtils';

/**
 * Convert PowerProfileDataPoint[] to CSV format expected by backend
 * Format: week_day,time,power_demand_kw
 * week_day: 0-6 (0=Monday, 1=Tuesday, ..., 6=Sunday)
 * time: HH:MM:SS (24h format, 1s intervals)
 * power_demand_kw: float value
 */
export const convertPowerProfileDataToCsv = (dataPoints: PowerProfileDataPoint[]): string => {
  if (!dataPoints || dataPoints.length === 0) {
    return '';
  }

  const csvRows = ['week_day,time,power_demand_kw'];

  dataPoints.forEach((point) => {
    let time = '00:00';

    if (point.timestamp.includes('T')) {
      // ISO format - parse as Date
      const date = new Date(point.timestamp);
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      const seconds = date.getUTCSeconds().toString().padStart(2, '0');
      time = `${hours}:${minutes}:${seconds}`;
    } else if (point.timestamp.includes(':')) {
      // Custom format "dayIndex:time" where time can be HH:MM or HH:MM:SS
      const parts = point.timestamp.split(':');
      // Skip first part (dayIndex), join the rest to get the time
      time = parts.slice(1).join(':');
    }

    // Use dayIndex directly (0-6)
    const weekDay = point.dayIndex;

    // Format energy value
    const energy = point.power.toFixed(2);

    csvRows.push(`${weekDay},${time},${energy}`);
  });

  return csvRows.join('\n');
};

/**
 * Generate power profile data rows for a single day
 * @internal - Used internally by generateManualModeCsv and generateSingleDayCsv
 */
const generateDayRows = (
  dayIndex: number,
  isWorkingDay: boolean,
  workingHoursStart: string,
  workingHoursEnd: string,
  peakPowerKw: number,
  averagePowerKw: number,
  peaksPerDay?: number,
  peaksDuration?: number,
  restDayAveragePowerKw?: number
): string[] => {
  const rows: string[] = [];

  // Parse working hours
  const [startHour, startMin] = workingHoursStart.split(':').map(Number);
  const [endHour, endMin] = workingHoursEnd.split(':').map(Number);
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  const workingDurationMinutes = endMinutes - startMinutes;

  // Calculate peak intervals if peaks are defined
  let peakIntervals: Array<{ start: number; end: number }> = [];
  if (peaksPerDay && peaksPerDay > 0 && peaksDuration && peaksDuration > 0) {
    const peakDurationMinutes = peaksDuration;
    const totalPeakMinutes = peaksPerDay * peakDurationMinutes;

    if (totalPeakMinutes <= workingDurationMinutes) {
      // Calculate spacing between peaks
      const availableSpaceMinutes = workingDurationMinutes - totalPeakMinutes;
      const gapBetweenPeaks = availableSpaceMinutes / (peaksPerDay + 1);

      // Distribute peaks evenly
      for (let i = 0; i < peaksPerDay; i++) {
        const peakStartMinutes = startMinutes + gapBetweenPeaks * (i + 1) + peakDurationMinutes * i;
        const peakEndMinutes = peakStartMinutes + peakDurationMinutes;
        peakIntervals.push({ start: peakStartMinutes, end: peakEndMinutes });
      }
    } else {
      // If total peak duration exceeds working hours, treat entire working period as peak
      peakIntervals.push({ start: startMinutes, end: endMinutes });
    }
  }

  // Helper function to check if current time is within any peak interval
  const isInPeakInterval = (currentMinutes: number): boolean => {
    return peakIntervals.some(
      (interval) => currentMinutes >= interval.start && currentMinutes < interval.end
    );
  };

  // Generate data for each 1-minute interval
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 1) {
      const currentMinutes = hour * 60 + minute;
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

      let power: number;

      if (isWorkingDay) {
        // Working day
        if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
          // Within working hours
          if (peaksPerDay && peaksPerDay > 0 && isInPeakInterval(currentMinutes)) {
            // During peak period
            power = peakPowerKw;
          } else {
            // During working hours but not peak period
            power = averagePowerKw;
          }
        } else {
          // Outside working hours on working day
          power = restDayAveragePowerKw ?? averagePowerKw;
        }
      } else {
        // Non-working day (rest day)
        power = restDayAveragePowerKw ?? averagePowerKw;
      }

      rows.push(`${dayIndex},${time},${power.toFixed(2)}`);
    }
  }

  return rows;
};

/**
 * Generate CSV from manual mode inputs
 * Creates a week's worth of data based on peak/average power and working hours
 * Distributes peaks evenly throughout the working day
 */
export const generateManualModeCsv = (
  peakPowerKw: number,
  averagePowerKw: number,
  workingDays: string[],
  workingHoursStart: string,
  workingHoursEnd: string,
  peaksPerDay?: number,
  peaksDuration?: number,
  restDayAveragePowerKw?: number
): string => {
  const csvRows = ['week_day,time,power_demand_kw'];

  // Day names mapping (0=Monday, 1=Tuesday, ..., 6=Sunday)
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Generate data for each day of the week
  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    const dayName = dayNames[dayIndex];
    const isWorkingDay = workingDays.includes(dayName);

    const dayRows = generateDayRows(
      dayIndex,
      isWorkingDay,
      workingHoursStart,
      workingHoursEnd,
      peakPowerKw,
      averagePowerKw,
      peaksPerDay,
      peaksDuration,
      restDayAveragePowerKw
    );

    csvRows.push(...dayRows);
  }

  return csvRows.join('\n');
};

/**
 * Generate CSV for a single working day
 * Useful for visualizing just the working hours period in charts
 */
export const generateSingleDayCsv = (
  peakPowerKw: number,
  averagePowerKw: number,
  workingHoursStart: string,
  workingHoursEnd: string,
  peaksPerDay?: number,
  peaksDuration?: number,
): string => {
  const csvRows = ['week_day,time,power_demand_kw'];

  const dayRows = generateDayRows(
    0, // Use Monday (index 0)
    true, // Always treat as working day
    workingHoursStart,
    workingHoursEnd,
    peakPowerKw,
    averagePowerKw,
    peaksPerDay,
    peaksDuration,
    undefined
  );

  csvRows.push(...dayRows);
  return csvRows.join('\n');
};

/**
 * Convert CSV back to PowerProfileDataPoint[] for chart display
 * This ensures the chart displays exactly what we're sending to backend
 */
export const convertCsvToPowerProfileData = (csvString: string): PowerProfileDataPoint[] => {
  if (!csvString) {
    return [];
  }

  const lines = csvString.trim().split('\n');
  const dataPoints: PowerProfileDataPoint[] = [];

  // Skip header line
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) {
      continue;
    }

    const [weekDay, time, powerDemandKw] = line.split(',');
    const dayIndex = parseInt(weekDay);
    const day = allDays[dayIndex];
    const [hours] = time.split(':');
    const hour = parseInt(hours);

    // Use custom timestamp format: "dayIndex:HH:MM"
    const timestamp = `${dayIndex}:${time}`;

    dataPoints.push({
      timestamp,
      power: parseFloat(powerDemandKw),
      day,
      hour,
      dayIndex,
    });
  }

  return dataPoints;
};

/**
 * Get maximum power value from CSV string
 * Returns the peak power demand from the power profile CSV
 */
export const getPeakPowerFromCsv = (csvString: string): number | null => {
  if (!csvString) {
    return null;
  }

  const dataPoints = convertCsvToPowerProfileData(csvString);
  if (dataPoints.length === 0) {
    return null;
  }

  const perMinuteDataset = aggregateDataByMinute(dataPoints); // to avoid Maximum call stack size exceeded runtime error

  const maxPower = Math.max(...perMinuteDataset.map((point) => point.power));
  return maxPower;
};
